import { type FC, type RefObject, useCallback, useEffect, useMemo, useRef } from 'react';

import { HotTable, type HotTableRef } from '@/components/UI/SpreadSheet/HotTable';
import {
  isHotInstance,
  useTreatmentGridValidation,
} from '@/components/Modals/treatmentGrid/useTreatmentGridValidation';
import { registerHOTModules, rowHeaderWithControlsRenderer } from '@/support/hot';
import { TREATMENT_NAME_ERRORS, type TreatmentTypeRow } from '@/utils/treatmentTypeValidation';

import { buildColumns, initBulkAddTreatmentsSettings } from './BulkAddTreatments.utils';

registerHOTModules();

/** Trailing blank row Handsontable keeps for the next entry; excluded from validation and saves. */
const SPARE_ROWS = 1;

interface HotTableRefLike {
  hotInstance?: unknown;
}

interface BulkAddTreatmentsTableProps {
  innerRef: RefObject<HotTableRefLike | null>;
  setInvalid: (invalid: boolean) => void;
}

/**
 * Spreadsheet grid for creating treatment types in bulk. Rows can be added, pasted and removed;
 * the trailing spare row is excluded from validation and from the save payload.
 */
const BulkAddTreatmentsTable: FC<BulkAddTreatmentsTableProps> = ({ innerRef, setInvalid }) => {
  const columns = useMemo(() => buildColumns(), []);
  const initialData = useRef<Array<TreatmentTypeRow>>([]);
  const settings = useMemo(() => initBulkAddTreatmentsSettings(columns), [columns]);

  const { afterValidate, afterChange, afterRemoveRow, afterUndoRedo, afterRenderer, revalidate } =
    useTreatmentGridValidation({
      hotRef: innerRef,
      setInvalid,
      spareRows: SPARE_ROWS,
      fallbackMessage: TREATMENT_NAME_ERRORS.notValid,
    });

  useEffect(() => {
    revalidate();
  }, [revalidate]);

  /**
   * Moves the caret into a row Handsontable has just appended, so typing continues uninterrupted.
   *
   * Reached through the ref rather than through `this`: the first spare row is created while the
   * instance is still being constructed, and selecting a cell before its rendering engine exists
   * throws. The ref is only assigned once construction has finished.
   */
  const afterCreateRow = useCallback(
    (index: number, _amount: number, source?: string): void => {
      const hot = innerRef.current?.hotInstance;
      if (source === 'auto' && isHotInstance(hot)) {
        hot.selectCell(index, 0);
      }
    },
    [innerRef]
  );

  return (
    <div className="mv3 w-100 overflow-auto ow-spreadsheet-styles">
      <div className="hotSpreadSheet mt3 spreadSheet" data-testid="bulk-add-treatments__hot">
        <div className="ui-card spreedSheet" data-testid="bulk-add-treatments__hot-table">
          <HotTable
            ref={innerRef as RefObject<HotTableRef>}
            data={initialData.current}
            licenseKey={AppConfig.handsOnTableLicence}
            headerClassName="custom-header"
            fillHandle={false}
            afterValidate={afterValidate}
            afterChange={afterChange}
            afterRemoveRow={afterRemoveRow}
            afterUndo={afterUndoRedo}
            afterRedo={afterUndoRedo}
            afterRenderer={afterRenderer}
            afterCreateRow={afterCreateRow}
            afterGetRowHeader={rowHeaderWithControlsRenderer}
            testId="bulk-add-treatments__hot-table-inner"
            {...settings}
          />
        </div>
      </div>
    </div>
  );
};

export default BulkAddTreatmentsTable;
